import { useTranslation } from 'react-i18next';
import { TriangleAlert } from 'lucide-react';
import { useScreenSize } from '../../hooks/useScreenSize';
import { useArticleDetail } from '~/features/catalog/routes/resources/ArticleDetailRoute';
import { Button, buttonVariants } from '../ui/button';
import { TooltipTrigger, TooltipContent, Tooltip } from '../ui/tooltip';
import { CircularProgress } from '../CircularProgress';
import { SupplierInfo } from './SupplierInfo';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '~/common/components/ui/alert-dialog';
import { cn } from '~/common/utils/styleUtil';

export interface CartPositionProfitPlusButtonProps {
  articleNumber: string;
  useTrigger?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  showFooter?: boolean;
}

export function ProfitPlusDialog({
  articleNumber,
  useTrigger,
  onClose,
  onConfirm,
  showFooter
}: Readonly<CartPositionProfitPlusButtonProps>) {
  const { t } = useTranslation();
  const { isMobile } = useScreenSize();
  const { article, isLoading } = useArticleDetail(articleNumber);
  const [isOpen, setIsOpen] = useState(useTrigger !== true);

  function handleOnOpenChange(value: boolean) {
    setIsOpen(value);
    if (onClose) {
      onClose();
    }
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOnOpenChange}>
      {useTrigger && (
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" onClick={() => setIsOpen(true)}>
                <TriangleAlert className="w-6 h-6 text-orange-500" />
              </Button>
            </AlertDialogTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <>
              <div data-testid={'special-info'}>{t('Es gelten spezielle Preis- und Lieferkonditionen!')}</div>
              <div data-testid={'return-info'}>{t('Kein Rückgaberecht')}</div>
            </>
          </TooltipContent>
        </Tooltip>
      )}
      <AlertDialogContent fullscreen={isMobile} className={cn(!isMobile && 'max-w-xl')}>
        <AlertDialogHeader className={'py-0'}>
          <AlertDialogTitle>{t('Es gelten spezielle Preis- und Lieferkonditionen!')}</AlertDialogTitle>
        </AlertDialogHeader>
        <CartPositionSupplierInfo article={article} isLoading={isLoading} className={'border-none h-[50px]'} />
        <AlertDialogFooter
          className={cn(
            'flex flex-col desktop:flex-row gap-2 desktop:gap-0 justify-end',
            isMobile && 'flex-col-reverse gap-1'
          )}
        >
          <AlertDialogCancel
            className={cn(
              buttonVariants({ variant: showFooter ? 'primaryOutline' : 'default' }),
              'w-full desktop:w-fit'
            )}
            data-testid={'add-to-cart-cancel-button'}
          >
            {t(showFooter ? 'Abbrechen' : 'Ok')}
          </AlertDialogCancel>
          {showFooter && (
            <AlertDialogAction
              className={cn('w-full desktop:w-fit')}
              onClick={event => {
                event.stopPropagation();
                if (onConfirm) {
                  onConfirm();
                }
              }}
              data-testid={'add-to-cart-confirm-button'}
            >
              {t('Bestätigen')}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function CartPositionSupplierInfo({
  article,
  isLoading,
  className
}: Readonly<{
  article: {
    supplierGroupMinOrderValue?: number;
    supplierGroupName: string;
    supplierGroupTermsDocumentName: string;
    supplierGroupTermsDocumentUrl?: string;
  };
  isLoading: boolean;
  className: string;
}>) {
  if (isLoading || !article) {
    return <CircularProgress size={'lg'} />;
  }
  return (
    <div className={'my-2'}>
      <SupplierInfo article={article} className={className} />
    </div>
  );
}
